import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

// Full organization interface from the API
interface ApiOrganization {
  organization_id: string;
  organization_code: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

// Organization data for form submissions
interface OrganizationFormData {
  organization_id?: string;
  organization_code: string;
  name: string;
  description?: string;
}

interface V3OrganizationFormProps {
  organization?: ApiOrganization;
  onSubmit: (organizationData: OrganizationFormData) => Promise<void>;
  onClose: () => void;
}

const V3OrganizationForm: React.FC<V3OrganizationFormProps> = ({ 
  organization, 
  onSubmit, 
  onClose 
}) => {
  const [organizationData, setOrganizationData] = useState<OrganizationFormData>({
    organization_code: '',
    name: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const isEditMode = !!organization?.organization_id;
  
  // If editing, load organization data
  useEffect(() => {
    if (organization) {
      setOrganizationData({
        organization_id: organization.organization_id,
        organization_code: organization.organization_code,
        name: organization.name,
        description: organization.description || ''
      });
    }
  }, [organization]);
  
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setOrganizationData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await onSubmit(organizationData);
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error(err);
      setLoading(false);
    }
  };
  
  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditMode ? 'Edit Organization' : 'Create New Organization'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6">
            {error && (
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg text-red-800 mb-6">
                {error}
              </div>
            )}
            
            <div className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Organization Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={organizationData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="organization_code" className="block text-sm font-medium text-gray-700 mb-1">
                  Organization Code <span className="text-red-500">*</span>
                  {isEditMode && <span className="text-gray-500 text-xs ml-1">(Cannot be changed)</span>}
                </label>
                <input
                  type="text"
                  id="organization_code"
                  name="organization_code"
                  value={organizationData.organization_code}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    isEditMode ? 'bg-gray-50 cursor-not-allowed' : ''
                  }`}
                  required
                  readOnly={isEditMode}
                  disabled={isEditMode}
                />
                {!isEditMode && (
                  <p className="mt-1 text-xs text-gray-500">
                    This code will be used for login and must be unique
                  </p>
                )}
              </div>
              
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={organizationData.description || ''}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                  placeholder="Enter a brief description of the organization (optional)"
                />
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:bg-blue-400"
                disabled={loading}
              >
                {loading ? 'Saving...' : isEditMode ? 'Update Organization' : 'Create Organization'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default V3OrganizationForm;